import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  TextField, Switch, FormControlLabel, Box, Button, Tooltip, IconButton,
  Typography, InputAdornment, Grid, 
  Card, CardContent, CardHeader
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useTranslation } from 'react-i18next';
import useActionSettings from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';

const SettingBrowseWeb = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState('');
  const [tag, setTag] = useState('');
  const [startingUrl, setStartingUrl] = useState('');
  const [maxIterations, setMaxIterations] = useState(10);
  const [headless, setHeadless] = useState(true);

  const instructionsRef = useRef(null);

  // New state for browse web specific dropdown
  const [browseAnchorEl, setBrowseAnchorEl] = useState(null);
  const [browseMenuType, setBrowseMenuType] = useState('');

  // Use the unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'browseweb',
    onSaveStatusChange,
    customComparison: (initial) => {
      return (
        instructions === (initial.instructions || '') &&
        tag === (initial.tag || '') &&
        startingUrl === (initial.startingUrl || '') &&
        maxIterations === (initial.maxIterations || 10) &&
        headless === (initial.headless !== undefined ? initial.headless : true)
      );
    },
    customSaveData: () => ({
      tag,
      startingUrl,
      instructions,
      maxIterations,
      headless
    }),
    loadDataFunction: (data) => {
      console.log('Loading browse web data:', data);
      
      // Parse the data field if it's a JSON string
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      console.log('Parsed browse web data:', parsedData);
      
      // Update component state with loaded data
      if (parsedData.tag !== undefined) {
        setTag(parsedData.tag);
      }
      if (parsedData.startingUrl !== undefined) {
        setStartingUrl(parsedData.startingUrl);
      }
      if (parsedData.instructions !== undefined) {
        setInstructions(parsedData.instructions);
      }
      if (parsedData.maxIterations !== undefined) {
        setMaxIterations(parsedData.maxIterations);
      }
      if (parsedData.headless !== undefined) {
        setHeadless(parsedData.headless);
      }
    }
  });


  // Handle custom save with additional browse web data
  const handleSave = () => {
    const settingsData = {
      tag,
      startingUrl,
      instructions,
      maxIterations,
      headless,
      useConditions: true,
      conditions
    };
    
    baseHandleSave(settingsData);
  };

  // Update save disabled state when custom fields change
  useEffect(() => {
    updateSaveDisabledState();
  }, [instructions, tag, startingUrl, maxIterations, headless, updateSaveDisabledState]);

  // New handlers for the browse web specific dropdown
  const handleBrowseMenuOpen = (event, type) => {
    setBrowseAnchorEl(event.currentTarget);
    setBrowseMenuType(type);
  };

  const handleBrowseMenuClose = () => {
    setBrowseAnchorEl(null);
  };

  const handleBrowseTagSelect = (value) => {
    if (browseMenuType === 'tag') {
      const tagToInsert = `{${value}}`;
      setTag(prev => prev + tagToInsert);
    } else if (browseMenuType === 'startingUrl') {
      const tagToInsert = `{${value}}`;
      setStartingUrl(prev => prev + tagToInsert);
    } else if (browseMenuType === 'instructions') {
      instructionsRef.current?.insertTag(value);
    }
    handleBrowseMenuClose();
  };


  return (
    <ActionSettingsLayout
      // State
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('settingBrowseWeb.title', 'Browse Web Automation')}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      primaryColor="#5757b0"
      
      // Conditions
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      
      // Status window
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      
      // Tags dropdown
      tagsDropdownProps={{
        tags: tags,
        anchorEl: browseAnchorEl,
        handleTagSelect: handleBrowseTagSelect,
        setAnchorEl: handleBrowseMenuClose,
        menuType: browseMenuType
      }}
    >
      {/* Action-specific content - only the form fields unique to Browse Web */}
      
      {/* Tag Input */}
      <Grid item xs={12}>
        <Card sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: '1px solid #5757b010',
                  overflow: 'hidden',
                }}>
                  <CardHeader
                    title={
                      <Typography sx={{
                        fontWeight: 700,
                        color: '#444',
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1
                      }}>
                        <Box component="span" sx={{
                          bgcolor: '#5757b0',
                          color: 'white',
                          borderRadius: '6px',
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.7 },
                          mr: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          letterSpacing: '0.5px',
                          fontWeight: 600,
                          boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                        }}>
                          STORE
                        </Box>
                        {t('browse_web.tag_label')}
                      </Typography>
                    }
                    sx={{
                      borderBottom: '1px solid #f0f0f8',
                      backgroundColor: '#fafbff',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                      p: { xs: 1.5, sm: 2.5 },
                    }}
                  />
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    <TagEditor
                      value={tag}
                      onChange={setTag}
                      placeholder={t('browse_web.tag_placeholder')}
                      singleLine
                      onTagClick={(e) => handleBrowseMenuOpen(e, 'tag')}
                      tags={tags}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Starting URL */}
              <Grid item xs={12}>
                <Card sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: '1px solid #5757b010',
                  overflow: 'hidden',
                }}>
                  <CardHeader
                    title={
                      <Typography sx={{
                        fontWeight: 700,
                        color: '#444',
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1
                      }}>
                        <Box component="span" sx={{
                          bgcolor: '#5757b0',
                          color: 'white',
                          borderRadius: '6px',
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.7 },
                          mr: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          letterSpacing: '0.5px',
                          fontWeight: 600,
                          boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                        }}>
                          URL
                        </Box>
                        {t('action_common.starting_url')}
                      </Typography>
                    }
                    sx={{
                      borderBottom: '1px solid #f0f0f8',
                      backgroundColor: '#fafbff',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                      p: { xs: 1.5, sm: 2.5 },
                    }}
                  />
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    <TagEditor
                      value={startingUrl}
                      onChange={setStartingUrl}
                      placeholder={t('action_common.url_placeholder')}
                      singleLine
                      onTagClick={(e) => handleBrowseMenuOpen(e, 'startingUrl')}
                      tags={tags}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Instructions */}
              <Grid item xs={12}>
                <Card sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: '1px solid #5757b010',
                  overflow: 'hidden',
                }}>
                  <CardHeader
                    title={
                      <Typography sx={{
                        fontWeight: 700,
                        color: '#444',
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1
                      }}>
                        <Box component="span" sx={{
                          bgcolor: '#5757b0',
                          color: 'white',
                          borderRadius: '6px',
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.7 },
                          mr: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          letterSpacing: '0.5px',
                          fontWeight: 600,
                          boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                        }}>
                          DO
                        </Box>
                        {t('browse_web.instructions_label')}
                      </Typography>
                    }
                    sx={{
                      borderBottom: '1px solid #f0f0f8',
                      backgroundColor: '#fafbff',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                      p: { xs: 1.5, sm: 2.5 },
                    }}
                  />
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    <TagEditor
                      ref={instructionsRef}
                      value={instructions}
                      onChange={setInstructions}
                      placeholder={t('browse_web.instructions_placeholder')}
                      minHeight="150px"
                      onTagClick={(e) => handleBrowseMenuOpen(e, 'instructions')}
                      tags={tags}
                    />
                  </CardContent>
                </Card>
              </Grid>              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('action_common.max_iterations')}
                  value={maxIterations}
                  onChange={(e) => setMaxIterations(parseInt(e.target.value) || 10)}
                  variant="outlined"
                  helperText={t('action_common.max_iterations_help')}
                  InputProps={{
                    inputProps: { min: 1, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Card sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: '1px solid #5757b010',
                  overflow: 'hidden',
                }}>
                  <CardHeader
                    title={
                      <Typography sx={{
                        fontWeight: { xs: '1rem', sm: '1.25rem' },
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1
                      }}>
                        <Box component="span" sx={{
                          bgcolor: '#5757b0',
                          color: 'white',
                          borderRadius: '6px',
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.7 },
                          mr: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          letterSpacing: '0.5px',
                          fontWeight: 600,
                          boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                        }}>
                          BROWSER
                        </Box>
                        {t('action_common.browser_mode')}
                      </Typography>
                    }
                    sx={{
                      borderBottom: '1px solid #f0f0f8',
                      backgroundColor: '#fafbff',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                      p: { xs: 1.5, sm: 2.5 },
                    }}
                  />
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={headless}
                          onChange={(e) => setHeadless(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#5757b0',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#5757b0',
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('action_common.headless_mode')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                            {t('action_common.headless_mode_help')}
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
    </ActionSettingsLayout>
  );
};

export default SettingBrowseWeb;
